import { useState, useEffect, useCallback, useRef } from "react";

const ACTIVITY_EVENTS = [
  "mousemove",
  "keydown",
  "mousedown",
  "touchstart",
  "scroll",
  "click",
];

// Shared across every tab in this browser, so activity in ANY tab keeps the
// whole session alive — an idle background tab can no longer log out a tab the
// user is actively working in.
const LAST_ACTIVITY_KEY = "hd_last_activity";

// Don't write to localStorage on every mousemove — once every few seconds is
// plenty to keep the shared clock fresh without thrashing storage.
const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;
const TICK_MS = 1000;

/**
 * HIPAA §164.312(a)(2)(iii) — automatic logoff after inactivity.
 *
 * Defaults: 8-hour inactivity window with a 5-minute warning before logout.
 * Rationale: covers a full clinical shift without interruption; compensated
 * by mandatory 2FA, credentialed workforce-only access, and audit logging.
 *
 * Flow: idleMs of inactivity → warning dialog (warningMs countdown) → onIdle()
 *
 * Cross-tab: the "last activity" timestamp lives in localStorage and every tab
 * polls it, so all tabs share one idle clock. Polling wall-clock (instead of a
 * long setTimeout) also means a machine that wakes from sleep after the window
 * has elapsed logs out immediately, rather than resuming a paused timer.
 *
 * During the warning countdown, passive activity in THIS tab is ignored — the
 * user must click "Stay signed in". Genuine activity in another tab still
 * refreshes the shared clock and dismisses the warning everywhere.
 */
const useIdleTimer = ({
  onIdle,
  enabled = true,
  idleMs = 7 * 60 * 60 * 1000 + 55 * 60 * 1000, // 7h 55m before warning
  warningMs = 5 * 60 * 1000, // 5-minute countdown
}) => {
  const totalWarningSeconds = Math.floor(warningMs / 1000);
  const [isWarning, setIsWarning] = useState(false);
  const [countdown, setCountdown] = useState(totalWarningSeconds);

  const isWarningRef = useRef(false);
  const onIdleRef = useRef(onIdle);
  const lastWriteRef = useRef(0);
  const firedRef = useRef(false);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const readLastActivity = useCallback(() => {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const parsed = raw ? parseInt(raw, 10) : NaN;
    // Missing/corrupt value: treat now as the last activity so a bad localStorage
    // entry can never silently strand the user past the idle window.
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }, []);

  const writeActivity = useCallback((force = false) => {
    const now = Date.now();
    if (force || now - lastWriteRef.current >= ACTIVITY_WRITE_THROTTLE_MS) {
      lastWriteRef.current = now;
      localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    }
  }, []);

  // Exposed to the dialog's "Stay signed in" button. Refreshing the shared
  // timestamp clears the warning in every tab on their next tick.
  const extendSession = useCallback(() => {
    firedRef.current = false;
    isWarningRef.current = false;
    setIsWarning(false);
    setCountdown(totalWarningSeconds);
    writeActivity(true);
  }, [totalWarningSeconds, writeActivity]);

  useEffect(() => {
    if (!enabled) {
      isWarningRef.current = false;
      firedRef.current = false;
      setIsWarning(false);
      setCountdown(totalWarningSeconds);
      return;
    }

    // Seed the shared clock when the session becomes active.
    writeActivity(true);

    const handleActivity = () => {
      // While THIS tab shows the warning, require an explicit "Stay signed in".
      if (isWarningRef.current) return;
      writeActivity(false);
    };

    const tick = () => {
      const elapsed = Date.now() - readLastActivity();

      if (elapsed >= idleMs + warningMs) {
        if (!firedRef.current) {
          firedRef.current = true;
          onIdleRef.current?.();
        }
        return;
      }

      if (elapsed >= idleMs) {
        isWarningRef.current = true;
        setIsWarning(true);
        const remaining = Math.ceil((idleMs + warningMs - elapsed) / 1000);
        setCountdown(Math.max(0, remaining));
      } else {
        isWarningRef.current = false;
        setIsWarning(false);
        setCountdown(totalWarningSeconds);
      }
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true }),
    );

    tick();
    const interval = setInterval(tick, TICK_MS);

    return () => {
      clearInterval(interval);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
    };
  }, [enabled, idleMs, warningMs, totalWarningSeconds, readLastActivity, writeActivity]);

  return { isWarning, countdown, totalWarningSeconds, extendSession };
};

export default useIdleTimer;
